import React, { useState, useEffect, useCallback } from 'react';
import { useCustomer } from '../../contexts/CustomerContext';
import { supabase } from '../../utils/supabaseClient';
import {
  Bars3Icon,
  BellIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';

const Topbar = ({ onMenuClick }) => {
  const { customer, logout } = useCustomer();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // notifications.user_id is users.id (FK users(id)), not the auth uid; they differ for every
  // non-legacy account. current_app_user_id() resolves the caller's users.id server-side.
  const [appUserId, setAppUserId] = useState(null);

  useEffect(() => {
    setAppUserId(null);
    if (!customer?.auth_user_id) return;
    let cancelled = false;
    supabase.rpc('current_app_user_id').then(({ data }) => {
      if (!cancelled) setAppUserId(data || null);
    });
    return () => { cancelled = true; };
  }, [customer?.auth_user_id]);

  const fetchNotifications = useCallback(async () => {
    if (!appUserId) return;
    const { data } = await supabase
      .from('notifications')
      .select('id, title, message, status, created_at, action_url, data')
      .eq('user_id', appUserId)
      .order('created_at', { ascending: false })
      .limit(20);
    if (data) {
      setNotifications(data);
      setUnreadCount(data.filter(n => n.status === 'pending').length);
    }
  }, [appUserId]);

  // One fetch on mount, then realtime (notifications is in the supabase_realtime publication;
  // RLS users_own_notifications_select scopes events to this user). Was a 30 s poll.
  useEffect(() => {
    if (!appUserId) return;
    fetchNotifications();

    const apply = (update) => {
      setNotifications(prev => {
        const next = update(prev);
        setUnreadCount(next.filter(n => n.status === 'pending').length);
        return next;
      });
    };

    let needsResync = false;
    const channel = supabase
      .channel(`portal-notifications-${appUserId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${appUserId}` },
        (payload) => apply(prev => [payload.new, ...prev.filter(n => n.id !== payload.new.id)].slice(0, 20))
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'notifications', filter: `user_id=eq.${appUserId}` },
        (payload) => apply(prev => prev.map(n => (n.id === payload.new.id ? { ...n, ...payload.new } : n)))
      )
      // No DELETE listener: realtime cannot filter or RLS-scope DELETE events, so it would
      // deliver every notification deletion on the platform to every portal tab.
      .subscribe((status) => {
        // Events sent while the socket was down are not replayed — refetch once on reconnect.
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') needsResync = true;
        if (status === 'SUBSCRIBED' && needsResync) {
          needsResync = false;
          fetchNotifications();
        }
      });

    return () => { supabase.removeChannel(channel); };
  }, [appUserId, fetchNotifications]);

  const markAsRead = async (id) => {
    await supabase
      .from('notifications')
      .update({ status: 'read' })
      .eq('id', id);
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    if (!appUserId) return;
    await supabase
      .from('notifications')
      .update({ status: 'read' })
      .eq('user_id', appUserId)
      .eq('status', 'pending');
    setNotifications(prev => prev.map(n => ({ ...n, status: 'read' })));
    setUnreadCount(0);
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 bg-gradient-to-r from-slate-900/80 to-slate-900/60 dark:from-slate-900/90 dark:to-slate-900/70 backdrop-blur-xl border-b border-blue-500/20 dark:border-blue-900/30 shadow-lg safe-area-top">
      <div className="flex items-center justify-between px-4 py-3 lg:px-6">
        {/* Left side - Mobile menu button and logo */}
        <div className="flex items-center">
          <button
            onClick={onMenuClick}
            className="p-3 -ml-1 rounded-lg text-gray-400 hover:text-gray-300 hover:bg-blue-500/10 lg:hidden transition-colors touch-target"
            aria-label="Open menu"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="ml-2 lg:ml-0">
            <h1 className="text-xl font-semibold text-white">
              Customer Portal
            </h1>
            <p className="text-xs text-gray-400 dark:text-gray-500">Service Management</p>
          </div>
        </div>

        {/* Right side - Notifications and user menu */}
        <div className="flex items-center space-x-2 lg:space-x-4">
          {/* Notifications */}
          <div className="relative">
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="p-3 text-gray-400 hover:text-gray-300 hover:bg-blue-500/10 rounded-lg relative transition-colors touch-target"
              aria-label="Notifications"
            >
              <BellIcon className="h-6 w-6" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-bold ring-2 ring-slate-900">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {notificationsOpen && (
              <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-slate-800 dark:bg-slate-700 rounded-lg shadow-lg ring-1 ring-blue-500/20 dark:ring-blue-900/30 z-50 max-h-96 overflow-y-auto">
                <div className="p-3 border-b border-slate-700 flex items-center justify-between">
                  <h3 className="text-sm font-medium text-white">Notifications</h3>
                  {unreadCount > 0 && (
                    <button onClick={markAllRead} className="text-xs text-blue-400 hover:text-blue-300">
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="p-4">
                    <p className="text-sm text-gray-400">No notifications yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-700">
                    {notifications.map(n => (
                      <div
                        key={n.id}
                        className={`p-3 hover:bg-slate-700/50 cursor-pointer transition-colors ${n.status === 'pending' ? 'bg-blue-500/5' : ''}`}
                        onClick={() => {
                          if (n.status === 'pending') markAsRead(n.id);
                          // Only follow in-app paths ("/x", never "//host", "/\host" or "javascript:"): a
                          // notification's link is data another party wrote. Every live action_url is in-app (2026-10-24).
                          if (n.action_url && /^\/(?![/\\])/.test(n.action_url)) window.location.href = n.action_url;
                        }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium truncate ${n.status === 'pending' ? 'text-white' : 'text-gray-400'}`}>
                              {n.title}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{n.message}</p>
                            <p className="text-xs text-gray-500 mt-1">{timeAgo(n.created_at)}</p>
                          </div>
                          {n.status === 'pending' && (
                            <span className="ml-2 mt-1 h-2 w-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center space-x-2 p-2 text-gray-300 hover:text-white hover:bg-blue-500/10 rounded-lg transition-colors touch-target"
              aria-label="User menu"
            >
              <UserCircleIcon className="h-8 w-8 text-gray-400" />
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-white">{customer?.name || 'Customer'}</p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{customer?.email}</p>
              </div>
            </button>

            {/* User dropdown */}
            {userMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-slate-800 dark:bg-slate-700 rounded-md shadow-lg ring-1 ring-blue-500/20 dark:ring-blue-900/30 z-50">
                <div className="py-1">
                  <a
                    href="/profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-blue-500/10 transition-colors"
                  >
                    <UserCircleIcon className="h-4 w-4 mr-2" />
                    Profile
                  </a>
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-blue-500/10 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4 mr-2" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
